'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Printer, ScanLine, Trash2, Usb } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { Device, DeviceType } from '../types';

interface DeviceCardProps {
  device: Device;
  onEdit: (device: Device) => void;
  onDelete: (device: Device) => void;
}

const STATUS_STYLES: Record<string, string> = {
  online:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  offline: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  maintenance:
    'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
};

const TYPE_STYLES: Record<DeviceType, string> = {
  scanner:
    'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400',
  printer: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  barcode_scanner:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

const TYPE_ICON: Record<
  DeviceType,
  React.ComponentType<{ className?: string }>
> = {
  scanner: ScanLine,
  printer: Printer,
  barcode_scanner: Usb
};

export function DeviceCard({ device, onEdit, onDelete }: DeviceCardProps) {
  const t = useTranslations('devices');

  const statusLabel =
    device.status === 'online'
      ? t('statusOnline')
      : device.status === 'offline'
        ? t('statusOffline')
        : t('statusMaintenance');

  const deviceType: DeviceType = device.device_type ?? 'scanner';
  const typeLabel =
    deviceType === 'scanner'
      ? t('deviceTypeBadgeScanner')
      : deviceType === 'printer'
        ? t('deviceTypeBadgePrinter')
        : t('deviceTypeBadgeBarcodeScanner');
  const TypeIcon = TYPE_ICON[deviceType] ?? ScanLine;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-3 flex items-center gap-2'>
          <Badge
            variant='outline'
            className={cn('gap-1.5', TYPE_STYLES[deviceType])}
          >
            <TypeIcon className='h-3 w-3' />
            {typeLabel}
          </Badge>
          <Badge
            variant='outline'
            className={cn('ms-auto shrink-0', STATUS_STYLES[device.status])}
          >
            {statusLabel}
          </Badge>
        </div>
        <div className='mb-4'>
          <h3 className='font-semibold'>{device.name}</h3>
          {device.location && (
            <p className='text-muted-foreground text-sm'>{device.location}</p>
          )}
        </div>

        <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
          <p>
            {t('serialNumber')}: {device.serial_number}
          </p>
          <p>
            {t('ipAddress')}: {device.ip_address}
          </p>
          {device.software_version && (
            <p>
              {t('softwareVersion')}: {device.software_version}
            </p>
          )}
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['devices.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(device)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['devices.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(device)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
